// Full-screen viewer (from components/profile/MediaLightbox). The only place a wall video plays on web
// (spec 3b §4 "Video on web": no dock, and the streams PiP is for live slots). Chat albums open here
// too, with the chat top bar (Figma Web / Chat / Media viewer / Album · Photo 4344:2571, · Video
// 4344:2536): counter, arrows, thumbnails and touch swipe across the album's photos and videos.
// Videos play under the shared full-screen controls (spec 2026-10-24 fullscreen player controls; Shared / Player /
// Controls 4351:2706 File video): play / pause and the seek bar over the video, hidden 4 s after the last
// interaction together with the viewer's arrows / thumbnails over the stage. The viewer is a page overlay, never the
// element Fullscreen API, so it works where that API is missing (iPhone Safari) with the same auto-hide.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Download, Pause, Play, X } from "lucide-react";
import type { WallItem } from "../api/content";
import { useAppOverlay } from "../store/appOverlayStore";
import VideoPlayer from "./VideoPlayer";
import { isVideoItem } from "./registry";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import FullScreenPlayerControls from "./controls/FullScreenPlayerControls";
import type { PlayerControlsSpec } from "./controls/playerControlsSpec";
import { usePlayerControlsVisibility, type PlayerControlsVisibility } from "./controls/usePlayerControlsVisibility";
import Img from "../components/ui/Img";
import { firstGrapheme } from "../emoji/graphemes";

/** A chat album's sender and actions, shown in the viewer's top bar. */
export interface ChatViewerInfo {
  senderName: string;
  avatar: string | null;
  timestamp: number;
  /** Closes the viewer back onto the message. */
  onShowInChat(): void;
}

interface Props {
  items: WallItem[];
  startIndex: number;
  onClose(): void;
  /** Chat mode: the chat top bar instead of the dark wall overlay. */
  chat?: ChatViewerInfo;
  /**
   * The browser can't decode the video at `index` (MEDIA_ERR_SRC_NOT_SUPPORTED / MEDIA_ERR_DECODE): the
   * opener marks it can't-play. A network error shows a retry here instead.
   */
  onVideoUnplayable?(index: number): void;
}

/** A swipe pages only past this horizontal distance (px) and when mostly horizontal (|dx| > 1.5·|dy|). */
const SWIPE_PX = 50;
const SWIPE_RATIO = 1.5;
/** Touches starting this close (px) to a video's bottom edge belong to its controls / seek bar. */
const VIDEO_CONTROLS_PX = 48;

/** A touch that starts on a video's controls (the seek bar, a button over it, or its bottom band) scrubs or taps them: no swipe. */
function startsOnVideoControls(target: EventTarget | null, clientY: number): boolean {
  if ((target as HTMLElement | null)?.closest?.("[data-player-chrome] button, [role='slider']")) return true;
  const video = (target as HTMLElement | null)?.closest?.("video");
  if (!video) return false;
  return clientY >= video.getBoundingClientRect().bottom - VIDEO_CONTROLS_PX;
}

/**
 * The viewer is portaled from inside a message row: React events still bubble to the row's handlers
 * (long-press, double-click reply, right-click reactions). Stop them at the viewer root.
 */
const stop = (e: React.SyntheticEvent) => e.stopPropagation();
const ISOLATE = {
  onPointerDown: stop,
  onPointerUp: stop,
  onMouseDown: stop,
  onMouseUp: stop,
  onContextMenu: stop,
  onDoubleClick: stop,
};

export function formatDurationMs(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = String(total % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
}

const ARROW = "absolute top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 text-white flex items-center justify-center"; // theme-exempt: overlay on the black backdrop
const CLOSE = "absolute right-4 top-4 w-10 h-10 rounded-full bg-white/15 text-white flex items-center justify-center"; // theme-exempt

const MEDIA_ERR_DECODE = 3;
const MEDIA_ERR_SRC_NOT_SUPPORTED = 4;

/** Hidden with the video controls: nothing of ours over the video (and out of the tab order / a11y tree). */
const fadeWith = (vis: PlayerControlsVisibility, video: boolean) =>
  video && !vis.visible ? { className: "opacity-0 pointer-events-none", inert: true, "aria-hidden": true as const } : { className: "opacity-100" };

/** A viewer video under the shared controls: play / pause (large) and the seek bar; up while paused. */
function ViewerVideo({ src, poster, vis, onError }: { src?: string; poster?: string; vis: PlayerControlsVisibility; onError(e: React.SyntheticEvent<HTMLVideoElement>): void }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLVideoElement>(null);
  // Paused until it plays (autoplay may be refused): the controls stay up with Play.
  const [paused, setPaused] = useState(true);
  const [p, setP] = useState({ pos: 0, dur: 0, buf: 0 });
  const { hold } = vis;
  useEffect(() => hold("paused", paused), [hold, paused]);
  // The viewer's visibility outlives this keyed video: release its hold when the item changes.
  useEffect(() => () => hold("paused", false), [hold]);
  const sync = () => {
    const el = ref.current;
    if (!el) return;
    const b = el.buffered;
    setP({
      pos: el.currentTime * 1000,
      dur: Number.isFinite(el.duration) ? el.duration * 1000 : 0,
      buf: b && b.length > 0 ? b.end(b.length - 1) * 1000 : 0,
    });
  };
  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) void el.play()?.catch?.(() => {});
    else el.pause();
  };
  const spec: PlayerControlsSpec = {
    // The viewer's own top bar (sender, counter, close) stays; the shared bars add play / pause and the seek bar.
    title: "",
    top: [],
    middle: [{
      testId: "viewer-video-playpause",
      label: paused ? t("dock_play", "Play") : t("dock_pause", "Pause"),
      icon: paused ? <Play aria-hidden="true" /> : <Pause aria-hidden="true" />,
      onClick: toggle,
      variant: "large",
    }],
    seek: {
      positionMs: p.pos,
      bufferedMs: p.buf,
      durationMs: p.dur,
      draggable: true,
      onSeek: (ms) => {
        if (ref.current) ref.current.currentTime = ms / 1000;
        sync();
      },
    },
  };
  return (
    <FullScreenPlayerControls spec={spec} visibility={vis}>
      <VideoPlayer
        ref={ref}
        testId="viewer-video"
        src={src}
        poster={poster}
        autoPlay
        playsInline
        onPlay={() => setPaused(false)}
        onPause={() => setPaused(true)}
        onEnded={() => setPaused(true)}
        onTimeUpdate={sync}
        onProgress={sync}
        onDurationChange={sync}
        onLoadedMetadata={sync}
        onError={onError}
        className="size-full bg-black object-contain" // theme-exempt: video letterbox
      />
    </FullScreenPlayerControls>
  );
}

export default function MediaViewer({ items, startIndex, onClose, chat, onVideoUnplayable }: Props) {
  const { t } = useTranslation();
  // Covers the dock: a wall YouTube / SoundCloud item holds (and never plays over a file video here).
  useAppOverlay();
  const [idx, setIdxRaw] = useState(startIndex);
  const count = items.length;
  // A network failure: "Couldn't load · Tap to retry"; each retry remounts the <video>.
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // Videos this browser can't decode: "Can't play here" in place of the player.
  const [unplayable, setUnplayable] = useState<ReadonlySet<number>>(new Set());
  const setIdx = (next: number | ((i: number) => number)) => {
    setLoadFailed(false);
    setIdxRaw(next);
  };
  const onVideoError = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const code = e.currentTarget.error?.code;
    if (code === MEDIA_ERR_DECODE || code === MEDIA_ERR_SRC_NOT_SUPPORTED) {
      setUnplayable((s) => new Set(s).add(idx));
      onVideoUnplayable?.(idx);
    } else setLoadFailed(true);
  };
  // Touch swipe: left → next, right → previous (arrows and keys stay). One finger, mostly horizontal,
  // not on a video's controls; the touch never reaches the message row behind the viewer.
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const swipe = {
    onTouchStart: (e: React.TouchEvent) => {
      e.stopPropagation();
      const touch = e.touches[0];
      touchStart.current =
        e.touches.length === 1 && touch && !startsOnVideoControls(e.target, touch.clientY)
          ? { x: touch.clientX, y: touch.clientY }
          : null;
    },
    onTouchMove: (e: React.TouchEvent) => {
      e.stopPropagation();
      if (e.touches.length > 1) touchStart.current = null; // a pinch, not a swipe
    },
    onTouchCancel: (e: React.TouchEvent) => {
      e.stopPropagation();
      touchStart.current = null;
    },
    onTouchEnd: (e: React.TouchEvent) => {
      e.stopPropagation();
      const start = touchStart.current;
      touchStart.current = null;
      const end = e.changedTouches[0];
      if (!start || !end || e.touches.length > 0) return;
      const dx = end.clientX - start.x;
      const dy = end.clientY - start.y;
      if (Math.abs(dx) <= SWIPE_PX || Math.abs(dx) <= SWIPE_RATIO * Math.abs(dy)) return;
      if (dx < 0) setIdx((i) => Math.min(count - 1, i + 1));
      else setIdx((i) => Math.max(0, i - 1));
    },
  };

  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  // The shared controls' visibility for a video item (a new item / retry shows them once).
  const cur = items[idx];
  const isVideo = (cur != null && isVideoItem(cur)) && !unplayable.has(idx);
  const vis = usePlayerControlsVisibility({ active: isVideo, itemKey: `${cur?.id}-${idx}-${attempt}` });
  const showRef = useRef(vis.show);
  showRef.current = vis.show;
  // The arrows / thumbnails / close fade with the video controls but sit outside their root: keyboard focus on them
  // holds the controls too (else they turn inert under the focus). Keys anywhere in the viewer count as keyboard use;
  // the focus handlers go on each faded element only (the chat top bar never fades, so focus there holds nothing).
  // Pointer handlers stay on the controls' root only.
  const chromeKeys = { onKeyDown: vis.bind.onKeyDown };
  const chromeFocus = { onFocusCapture: vis.bind.onFocusCapture, onBlurCapture: vis.bind.onBlurCapture };

  // Focus the close button on open; give focus back to whatever opened the viewer on close.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => opener?.focus?.();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      // Any key shows a video's controls (a no-op for photos).
      showRef.current();
      if (e.key === "Tab") {
        // Hidden controls are inert: never a Tab stop.
        const nodes = [...(dialogRef.current?.querySelectorAll<HTMLElement>("button, a[href], video[controls], [role='slider']") ?? [])].filter((n) => !n.closest("[inert]"));
        if (nodes.length === 0) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        const active = document.activeElement;
        if (!dialogRef.current?.contains(active)) {
          e.preventDefault();
          first.focus();
        } else if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
        return;
      }
      // Controls with their own arrow-key behaviour (video seek, inputs) keep it; buttons (Close is
      // focused on open, arrows / thumbnails after a click) don't use arrows, so the viewer pages.
      const target = e.target as HTMLElement | null;
      if (target?.closest?.("video, input, textarea, select, [role='slider']")) return;
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
      if (e.key === "ArrowRight") setIdx((i) => Math.min(count - 1, i + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [count, onClose]);

  const it = items[idx];
  if (!it) return null;
  const fade = { ...fadeWith(vis, isVideo), ...chromeFocus };

  if (chat) {
    const when = new Date(chat.timestamp);
    const meta = [
      when.toLocaleDateString([], { month: "short", day: "numeric" }),
      when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      isVideoItem(it) && it.durationMs ? formatDurationMs(it.durationMs) : null,
    ]
      .filter(Boolean)
      .join(" · ");
    const NAV = "absolute top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-[var(--bg-surface)] border border-[var(--border)] text-[var(--foreground)] flex items-center justify-center cursor-pointer p-0";
    return createPortal(
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={t("wall_media_viewer")} data-testid="media-viewer" data-media-viewer className="fixed inset-0 z-[2100] flex flex-col bg-[var(--bg-base)]" {...ISOLATE} {...chromeKeys} onClick={stop} onTouchStart={stop} onTouchMove={stop} onTouchEnd={stop} onTouchCancel={stop}>
        <div className="flex items-center gap-3 px-4 py-3 bg-[var(--bg-surface)] border-b border-[var(--border)]">
          <button ref={closeRef} type="button" aria-label={t("viewer_close")} className="flex items-center justify-center bg-transparent border-none cursor-pointer text-[var(--foreground)] p-0" onClick={onClose}>
            <X size={24} />
          </button>
          <Avatar className="h-8 w-8 shrink-0">
            <AvatarImage src={chat.avatar ?? undefined} alt={chat.senderName} />
            <AvatarFallback className="text-xs">{(firstGrapheme(chat.senderName) || "?").toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col flex-1 min-w-0 overflow-hidden whitespace-nowrap">
            <span className="text-base font-bold leading-[1.2] text-[var(--foreground)] truncate">{chat.senderName}</span>
            <span className="text-xs leading-[1.3] text-[var(--foreground-muted)] truncate">{meta}</span>
          </div>
          {count > 1 && (
            <span data-testid="viewer-counter" className="shrink-0 rounded-full px-[10px] py-1 bg-[var(--bg-elevated)] text-sm font-bold leading-none text-[var(--foreground)] whitespace-nowrap">
              {t("viewer_counter", { n: idx + 1, total: count })}
            </span>
          )}
          {it.photoUrl && (
            <a href={it.photoUrl} download aria-label={t("viewer_download")} className="flex items-center text-[var(--foreground)]">
              <Download size={24} />
            </a>
          )}
          <button type="button" className="bg-transparent border-none cursor-pointer text-sm font-bold text-[var(--primary-text)] whitespace-nowrap p-0" onClick={chat.onShowInChat}>
            {t("viewer_show_in_chat")}
          </button>
        </div>
        <div data-testid="viewer-stage" {...swipe} className={`relative flex flex-1 min-h-0 items-center justify-center px-5 pt-5 overflow-hidden ${count > 1 ? "pb-[88px]" : "pb-5"}`}>
          {isVideoItem(it) ? (
            unplayable.has(idx) ? (
              <span className="text-sm font-bold text-[var(--foreground-muted)]">{t("chat_video_cant_play")}</span>
            ) : (
              // key: switching items (or retrying) remounts the element, which stops playback
              <ViewerVideo key={`${it.id}-${idx}-${attempt}`} src={it.photoUrl ?? undefined} poster={it.thumbnailUrl ?? undefined} vis={vis} onError={onVideoError} />
            )
          ) : it.photoUrl ? (
            <Img key={`${it.id}-${idx}`} data-testid="viewer-photo" src={it.photoUrl} alt={`${t("chat_album_preview_photos", { count: 1 })} · ${t("viewer_counter", { n: idx + 1, total: count })}`} className="max-w-full max-h-full object-contain" />
          ) : null}
          {loadFailed && (
            <button
              type="button"
              data-testid="viewer-video-retry"
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-none cursor-pointer px-4 py-2 text-sm font-bold bg-black/60 text-white" // theme-exempt: label over the video
              onClick={() => {
                setLoadFailed(false);
                setAttempt((n) => n + 1);
              }}
            >
              {t("dock_chat_load_failed")}
            </button>
          )}
          {count > 1 && idx > 0 && (
            <button type="button" aria-label={t("viewer_previous")} {...fade} className={`${NAV} left-6 transition-opacity duration-200 ${fade.className}`} onClick={() => setIdx(idx - 1)}>
              <ChevronLeft size={28} />
            </button>
          )}
          {count > 1 && idx < count - 1 && (
            <button type="button" aria-label={t("viewer_next")} {...fade} className={`${NAV} right-6 transition-opacity duration-200 ${fade.className}`} onClick={() => setIdx(idx + 1)}>
              <ChevronRight size={28} />
            </button>
          )}
          {count > 1 && (
            <div data-testid="viewer-thumbnails" {...fade} className={`absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-full overflow-x-auto px-4 transition-opacity duration-200 ${fade.className}`}>
              {items.map((m, i) => {
                const thumb = m.thumbnailUrl ?? (isVideoItem(m) ? null : m.photoUrl);
                return (
                  <button
                    key={`${m.id}-${i}`}
                    type="button"
                    aria-label={t("wall_carousel_goto", { n: i + 1 })}
                    aria-current={i === idx ? "true" : undefined}
                    onClick={() => setIdx(i)}
                    className={`relative shrink-0 w-14 h-14 rounded-lg overflow-hidden p-0 cursor-pointer flex items-center justify-center bg-[var(--bg-elevated)] ${i === idx ? "border-2 border-[var(--primary-text)]" : "border-0 opacity-70"}`}
                  >
                    {thumb && <Img src={thumb} alt="" className="absolute inset-0 w-full h-full object-cover" />}
                    {isVideoItem(m) && <Play size={24} className="relative text-white" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={t("wall_media_viewer")} data-testid="media-viewer" data-media-viewer className="fixed inset-0 z-[2100] bg-black/90 flex items-center justify-center" {...ISOLATE} {...chromeKeys} {...swipe} onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <button ref={closeRef} type="button" aria-label={t("wall_close")} {...fade} className={`${CLOSE} transition-opacity duration-200 ${fade.className}`} onClick={(e) => { e.stopPropagation(); onClose(); }}>
        <X size={20} />
      </button>
      <div className={`${isVideoItem(it) ? "w-[92vw] h-[88vh]" : "max-w-[92vw] max-h-[88vh]"} flex items-center justify-center`} onClick={(e) => e.stopPropagation()}>
        {isVideoItem(it) ? (
          // key: switching items unmounts the element, which stops playback
          <ViewerVideo key={it.id} src={it.photoUrl ?? undefined} poster={it.thumbnailUrl ?? undefined} vis={vis} onError={() => {}} />
        ) : (
          <Img key={it.id} src={it.photoUrl ?? ""} alt="" className="max-w-[92vw] max-h-[88vh] object-contain" />
        )}
      </div>
      {count > 1 && (
        <>
          {idx > 0 && (
            <button type="button" aria-label={t("wall_carousel_prev")} {...fade} className={`${ARROW} left-4 transition-opacity duration-200 ${fade.className}`} onClick={(e) => { e.stopPropagation(); setIdx(idx - 1); }}>
              <ChevronLeft size={20} />
            </button>
          )}
          {idx < count - 1 && (
            <button type="button" aria-label={t("wall_carousel_next")} {...fade} className={`${ARROW} right-4 transition-opacity duration-200 ${fade.className}`} onClick={(e) => { e.stopPropagation(); setIdx(idx + 1); }}>
              <ChevronRight size={20} />
            </button>
          )}
          <span {...fade} className={`absolute bottom-4 left-1/2 -translate-x-1/2 text-sm text-white/80 transition-opacity duration-200 ${fade.className}`}>{t("wall_carousel_page", { n: idx + 1, total: count })}</span>
        </>
      )}
    </div>,
    document.body,
  );
}
